import { useState, useEffect, useRef } from 'react';
import { Input } from '@/components/ui/input';
import { Loader2, Search } from 'lucide-react';
import { searchEtablissement, type EtablissementSearchResult } from '@/api/schools';

interface EtablissementSearchInputProps {
  id?: string;
  placeholder?: string;
  onSelect: (result: EtablissementSearchResult) => void;
}

/**
 * Autocomplete sur l'annuaire Éducation Nationale (recherche par RNE ou nom).
 * Partagé entre le wizard de création d'école et la gestion des établissements.
 */
export function EtablissementSearchInput({
  id = 'etablissement-search',
  placeholder = "Code RNE ou nom de l'établissement...",
  onSelect,
}: EtablissementSearchInputProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [searchResults, setSearchResults] = useState<EtablissementSearchResult[]>([]);
  const [showResults, setShowResults] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);

  // Close results when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setShowResults(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Debounced autocomplete search
  useEffect(() => {
    if (searchQuery.length < 3) {
      setSearchResults([]);
      setShowResults(false);
      return;
    }

    const timeoutId = setTimeout(async () => {
      setSearching(true);
      try {
        const response = await searchEtablissement(searchQuery);
        setSearchResults(response.results);
        setShowResults(true);
      } catch (err) {
        console.error('Error searching:', err);
      } finally {
        setSearching(false);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  const handleSelect = (result: EtablissementSearchResult) => {
    onSelect(result);
    setShowResults(false);
    setSearchQuery('');
  };

  return (
    <div className="relative z-10" ref={searchRef}>
      <div className="relative">
        <Input
          id={id}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onFocus={() => searchResults.length > 0 && setShowResults(true)}
          placeholder={placeholder}
          className="pr-10"
        />
        {searching && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2">
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          </div>
        )}
        {!searching && searchQuery.length >= 3 && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2">
            <Search className="h-4 w-4 text-muted-foreground" />
          </div>
        )}
        {showResults && searchResults.length > 0 && (
          <div className="absolute z-50 w-full mt-1 bg-white border rounded-md shadow-lg max-h-60 overflow-auto">
            {searchResults.map((result, index) => (
              <button
                key={result.rne || index}
                type="button"
                className="w-full px-3 py-2 text-left hover:bg-muted border-b last:border-b-0"
                onClick={() => handleSelect(result)}
              >
                <div className="font-medium text-sm">{result.name || 'Sans nom'}</div>
                <div className="text-xs text-muted-foreground">
                  {result.rne && <span className="font-mono">{result.rne}</span>}
                  {result.city && <span> - {result.city}</span>}
                  {result.postalCode && <span> ({result.postalCode})</span>}
                </div>
              </button>
            ))}
          </div>
        )}
        {showResults && searchResults.length === 0 && !searching && searchQuery.length >= 3 && (
          <div className="absolute z-50 w-full mt-1 bg-white border rounded-md shadow-lg p-3 text-sm text-muted-foreground">
            Aucun résultat trouvé
          </div>
        )}
      </div>
      <p className="text-xs text-muted-foreground mt-2">
        Tapez au moins 3 caractères pour rechercher par code RNE ou nom
      </p>
    </div>
  );
}
